import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { AlertTriangle, Calendar, CheckCircle2, ChevronDown, ChevronUp, Clock, Hourglass, MessageSquare } from "lucide-react";

/**
 * AdminConcernsPanel — Admin/Owner "Concerns & Resolutions" workspace.
 *
 * Lists every customer concern with the full context the admin needs to
 * triage in one place: customer, concierge, booking, experience details,
 * priority + status pipeline. Admin can update status (New → Reviewing →
 * Waiting Response → Resolved) and append internal notes that customers
 * never see.
 *
 * Concierge involvement is NOT automatic — admin decides if/when to loop
 * the concierge in (via the existing /messages flow, outside this panel).
 */
const CONCERN_TYPE_LABELS = {
  concierge_issue: "Concierge issue",
  experience_issue: "Experience issue",
  payment_concern: "Payment concern",
  safety_concern: "Safety concern",
  cancellation_issue: "Cancellation issue",
  other: "Other",
};

const STATUS_ORDER = ["New", "Reviewing", "Waiting Response", "Resolved"];

const STATUS_ICON = {
  New: AlertTriangle,
  Reviewing: Hourglass,
  "Waiting Response": Clock,
  Resolved: CheckCircle2,
};

export default function AdminConcernsPanel() {
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/concerns")
      .then((r) => setConcerns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setConcerns([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (concernId, status) => {
    await api.patch(`/concerns/${concernId}`, { status });
    load();
  };

  const addNote = async (concernId, note) => {
    if (!note.trim()) return;
    await api.patch(`/concerns/${concernId}`, { internal_note: note });
    load();
  };

  return (
    <div data-testid="admin-concerns-panel">
      <div className="grid sm:grid-cols-4 gap-3 mb-5">
        {STATUS_ORDER.map((s) => (
          <CountTile
            key={s}
            label={s}
            value={concerns.filter((c) => c.status === s).length}
          />
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-[#071A2D]/60">Loading concerns…</div>
      ) : concerns.length === 0 ? (
        <div className="p-8 rounded-2xl card-luxury text-center text-[#071A2D]/60" data-testid="admin-concerns-empty">
          No customer concerns yet.
        </div>
      ) : (
        <div className="space-y-3">
          {concerns.map((c) => (
            <ConcernCard
              key={c.concern_id}
              concern={c}
              isOpen={expanded === c.concern_id}
              onToggle={() => setExpanded((cur) => (cur === c.concern_id ? null : c.concern_id))}
              onStatus={(status) => updateStatus(c.concern_id, status)}
              onNote={(note) => addNote(c.concern_id, note)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CountTile({ label, value }) {
  const Icon = STATUS_ICON[label] || AlertTriangle;
  return (
    <div className="p-4 rounded-2xl card-luxury flex items-center gap-3">
      <Icon size={18} className="text-[#064E3B]"/>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#071A2D]/55">{label}</div>
        <div className="font-serif text-xl">{value}</div>
      </div>
    </div>
  );
}

function ConcernCard({ concern, isOpen, onToggle, onStatus, onNote }) {
  const [note, setNote] = useState("");
  const isUrgent = concern.priority === "urgent";
  const customer = concern.customer || {};
  const concierge = concern.concierge || {};
  const booking = concern.booking || {};
  const experience = concern.experience || {};

  return (
    <div
      data-testid={`admin-concern-${concern.concern_id}`}
      className="rounded-2xl card-luxury overflow-hidden"
    >
      {/* Header row */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-5 flex items-start gap-3 flex-wrap"
      >
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#064E3B]">
              {CONCERN_TYPE_LABELS[concern.concern_type] || concern.concern_type}
            </span>
            {isUrgent && (
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5 rounded-md bg-[#FF7A45]/15 text-[#FF7A45]">
                Urgent
              </span>
            )}
          </div>
          <p className="text-sm text-[#071A2D] mt-1.5 line-clamp-2">{concern.description}</p>
          <div className="text-[11px] text-[#071A2D]/55 mt-2 flex flex-wrap gap-x-3 gap-y-1">
            <span>Customer: <span className="font-medium text-[#071A2D]/75">{customer.name || customer.email || "—"}</span></span>
            {concierge.name && <span>· Concierge: <span className="font-medium text-[#071A2D]/75">{concierge.name}</span></span>}
            {booking.date && <span><Calendar size={10} className="inline -mt-0.5 mr-0.5"/>{booking.date} · {booking.time}</span>}
            <span>· Submitted {new Date(concern.created_at).toLocaleDateString()}</span>
          </div>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-semibold shrink-0 ${
          concern.status === "Resolved"
            ? "bg-[#064E3B] text-[#FFF8EE]"
            : "bg-[#C9A45C]/20 text-[#5B3F18]"
        }`}>
          {concern.status}
        </span>
        {isOpen ? <ChevronUp size={16} className="text-[#071A2D]/40 mt-1"/> : <ChevronDown size={16} className="text-[#071A2D]/40 mt-1"/>}
      </button>

      {/* Expanded detail */}
      {isOpen && (
        <div className="px-5 pb-5 border-t border-[#071A2D]/8">
          <div className="grid md:grid-cols-2 gap-5 mt-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#071A2D]/55">Customer message</div>
              <p className="text-sm mt-1.5 leading-relaxed text-[#071A2D]/85 whitespace-pre-wrap">{concern.description}</p>
              {concern.photo_url && (
                <a href={concern.photo_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs underline text-[#064E3B]">
                  View attached photo →
                </a>
              )}
            </div>
            <div className="space-y-3">
              {experience.title && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#071A2D]/55">Experience</div>
                  <div className="text-sm font-medium mt-1">{experience.title}</div>
                  <div className="text-xs text-[#071A2D]/60">{experience.city}{experience.state ? `, ${experience.state}` : ""}</div>
                </div>
              )}
              {booking.amount && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#071A2D]/55">Booking</div>
                  <div className="text-xs text-[#071A2D]/75 mt-0.5">
                    {booking.guests} guests · ${booking.amount} · status {booking.status || "—"}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status updater */}
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-[#071A2D]/55 mb-1.5">Update status</div>
            <div className="flex flex-wrap gap-2">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onStatus(s)}
                  data-testid={`admin-concern-status-${concern.concern_id}-${s.toLowerCase().replace(" ", "-")}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    concern.status === s
                      ? "bg-[#064E3B] text-[#FFF8EE] border-[#064E3B]"
                      : "border-[#071A2D]/15 text-[#071A2D]/70 hover:border-[#064E3B] hover:text-[#064E3B]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Internal notes */}
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-[#071A2D]/55 mb-1.5">Internal notes (admin-only)</div>
            {(concern.internal_notes || []).length > 0 && (
              <div className="space-y-2 mb-3">
                {(concern.internal_notes || []).map((n) => (
                  <div
                    key={`${n.at}-${n.author_id || "admin"}`}
                    className="rounded-xl card-luxury-well p-3 text-xs"
                  >
                    <div className="text-[#071A2D]/55">
                      {n.author_name || "Admin"} · {new Date(n.at).toLocaleString()}
                    </div>
                    <div className="mt-1 text-[#071A2D]/85 whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                rows={2}
                placeholder="Add an internal note for the team…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid={`admin-concern-note-${concern.concern_id}`}
                className="kbd-input flex-1"
              />
              <button
                type="button"
                onClick={() => { onNote(note); setNote(""); }}
                disabled={!note.trim()}
                data-testid={`admin-concern-add-note-${concern.concern_id}`}
                className="btn-outline-emerald text-xs disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <MessageSquare size={11}/> Add note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
